/**
 * Tapping Experience.
 *
 * Routes:
 *   /practices/tapping             → explainer
 *   /practices/tapping/select      → 7 sessions list
 *   /practices/tapping/play/:id    → tapping session screen
 *
 * Session structure (4 phases):
 *   1. Acknowledge   — "Even though I feel..." (calm intro)
 *   2. Tapping seq.  — body outline with glowing points; one highlighted at a time
 *   3. Shift         — language softens
 *   4. Settle        — auto-transition to State Tuning Calm tone (30–60s)
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X, Pause, Play } from "lucide-react";
import PracticeExplainer from "./PracticeExplainer";

/* ----------------------------- EXPLAINER ----------------------------- */
export const TappingIntro = () => (
  <PracticeExplainer
    testId="tapping-intro"
    eyebrow="Tapping"
    title="Release What's In The Way"
    accent="rgba(215,195,225,0.62)"
    lines={[
      "When something is stuck in the body, words alone won't move it.",
      "By tapping gentle points on your body, you signal to the nervous system that it's safe to soften — and the resistance dissolves on its own.",
      "Choose what you'd like to release. The voice will guide you. Your only job is to follow.",
    ]}
    cta="Start Tapping"
    to="/practices/tapping/select"
  />
);

/* -------------------------- SESSION LIST ----------------------------- */
const SESSIONS = [
  { id: "release-rejection", title: "Release Rejection", sub: "Let go of what stung", duration: "4 min" },
  { id: "clear-hesitation", title: "Clear Hesitation", sub: "Move forward with ease", duration: "4 min" },
  { id: "let-go-pressure", title: "Let Go of Pressure", sub: "Release what's been weighing on you", duration: "5 min" },
  { id: "money-ease", title: "Money Ease", sub: "Soften the grip around money", duration: "5 min" },
  { id: "reset-confidence", title: "Reset Confidence", sub: "Return to your centre", duration: "4 min" },
  { id: "detach-outcome", title: "Detach From Outcome", sub: "Let it unfold", duration: "4 min" },
  { id: "calm-before-action", title: "Calm Before Action", sub: "Steady before you step in", duration: "3 min" },
];

const navyBg = {
  background: "radial-gradient(ellipse at 50% 22%, #0E1A2C 0%, #0A1424 32%, #060B16 68%, #03060D 100%)",
};

export const TappingSelect = () => {
  const navigate = useNavigate();
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setArrived(true), 80);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen relative overflow-hidden" style={navyBg} data-testid="tapping-select">
      <header className="relative z-10 px-5 pt-5 pb-2 flex items-center">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-white/[0.04] transition-colors" aria-label="Back" data-testid="tapping-select-back">
          <ArrowLeft size={20} className="text-white/55" />
        </button>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-6 pt-4 pb-32">
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
          transition={{ duration: 1.0 }}
          className="text-white/95 text-3xl text-center mt-4"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          What would you like to release?
        </motion.h1>
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: arrived ? 0.55 : 0 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="mx-auto mt-7 h-px w-24"
          style={{ background: "linear-gradient(90deg, transparent, rgba(215,195,225,0.55), transparent)" }}
        />

        <div className="mt-12 space-y-3">
          {SESSIONS.map((s, i) => (
            <motion.button
              key={s.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 14 }}
              transition={{ duration: 0.9, delay: 0.4 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ scale: 1.012 }}
              whileTap={{ scale: 0.985 }}
              onClick={() => navigate(`/practices/tapping/play/${s.id}`)}
              data-testid={`tapping-session-${s.id}`}
              className="w-full text-left rounded-2xl px-6 py-5 relative overflow-hidden"
              style={{
                background: "linear-gradient(140deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.012) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-white/90 text-lg" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}>
                  {s.title}
                </h3>
                <span className="text-white/30 text-[11px] tracking-[0.18em] uppercase whitespace-nowrap">{s.duration}</span>
              </div>
              <p className="mt-1 text-white/45 text-sm font-light">{s.sub}</p>
            </motion.button>
          ))}
        </div>
      </main>
    </div>
  );
};

/* ----------------------- TAPPING SESSION SCREEN ---------------------- */
/* The 11 tapping points specified by the user, mapped onto a body silhouette
   in normalised 0–1 coordinates. Order matches the recommended sequence so
   the listener moves naturally from face → chin → chest → arms. */
const TAPPING_POINTS = [
  { id: "eb_l", x: 0.43,  y: 0.115, label: "Above left eyebrow" },
  { id: "eb_r", x: 0.57,  y: 0.115, label: "Above right eyebrow" },
  { id: "tmp_l",x: 0.355, y: 0.135, label: "Left temple" },
  { id: "tmp_r",x: 0.645, y: 0.135, label: "Right temple" },
  { id: "un",   x: 0.50,  y: 0.205, label: "Under the nose" },
  { id: "ch",   x: 0.50,  y: 0.245, label: "On the chin" },
  { id: "cl_l", x: 0.435, y: 0.305, label: "Under left collarbone" },
  { id: "cl_r", x: 0.565, y: 0.305, label: "Under right collarbone" },
  { id: "ua_l", x: 0.245, y: 0.425, label: "Under left arm" },
  { id: "ua_r", x: 0.755, y: 0.425, label: "Under right arm" },
  { id: "cc",   x: 0.50,  y: 0.40,  label: "Centre of the chest" },
];

const SESSION_PHASES = (sessionId) => {
  const titles = {
    "release-rejection": { feel: "this rejection", shift: "I let it move through me" },
    "clear-hesitation": { feel: "this hesitation", shift: "I trust my next step" },
    "let-go-pressure": { feel: "this pressure", shift: "I let it soften" },
    "money-ease": { feel: "this tightness around money", shift: "I allow money to flow with ease" },
    "reset-confidence": { feel: "this self-doubt", shift: "I return to who I am" },
    "detach-outcome": { feel: "this need to control", shift: "I let it unfold in its own time" },
    "calm-before-action": { feel: "this nervous energy", shift: "I am steady" },
  };
  const map = titles[sessionId] || titles["reset-confidence"];
  // 11 tapping points, ~8s each → ~90s of touch work, plus settle.
  return [
    { phase: "acknowledge", text: `Even though I feel ${map.feel}...`, duration: 7 },
    { phase: "acknowledge", text: "I deeply and completely accept myself.", duration: 7 },
    { phase: "tapping", text: `I feel ${map.feel}`,             point: 0, duration: 8 },
    { phase: "tapping", text: `It's been here for a while`,      point: 1, duration: 8 },
    { phase: "tapping", text: `It's safe to feel it`,            point: 2, duration: 8 },
    { phase: "tapping", text: `I don't have to push it away`,    point: 3, duration: 8 },
    { phase: "tapping", text: `I can let it move`,               point: 4, duration: 8 },
    { phase: "shift",   text: `I let it soften`,                 point: 5, duration: 8 },
    { phase: "shift",   text: `I let it move through me`,        point: 6, duration: 8 },
    { phase: "shift",   text: map.shift,                          point: 7, duration: 8 },
    { phase: "shift",   text: `I am here.`,                       point: 8, duration: 8 },
    { phase: "shift",   text: `I am safe.`,                       point: 9, duration: 8 },
    { phase: "shift",   text: `I return to myself.`,              point: 10, duration: 8 },
    { phase: "settle",  text: `Just breathe`,                     duration: 8 },
    { phase: "settle",  text: `Let everything settle`,            duration: 10 },
    { phase: "settle",  text: `Stay here`,                        duration: 14 },
  ];
};

export const TappingPlay = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const phases = SESSION_PHASES(sessionId);
  const [stepIdx, setStepIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const calmAudioRef = useRef(null);
  const calmCtxRef = useRef(null);

  const current = phases[stepIdx] || phases[phases.length - 1];

  /* Step progression */
  useEffect(() => {
    if (paused || done) return;
    if (stepIdx >= phases.length - 1) {
      // last step → after duration, mark done
      const t = setTimeout(() => setDone(true), current.duration * 1000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStepIdx((i) => i + 1), current.duration * 1000);
    return () => clearTimeout(t);
  }, [stepIdx, paused, done, current.duration, phases.length]);

  /* When done → start a Calm tone (30–60s auto-settle) */
  useEffect(() => {
    if (!done) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      calmCtxRef.current = ctx;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.16, ctx.currentTime + 2.0);
      master.connect(ctx.destination);

      const osc1 = ctx.createOscillator();
      osc1.type = "sine";
      osc1.frequency.value = 432;
      const g1 = ctx.createGain(); g1.gain.value = 0.5;
      osc1.connect(g1).connect(master);
      osc1.start();

      const osc2 = ctx.createOscillator();
      osc2.type = "sine";
      osc2.frequency.value = 216;
      const g2 = ctx.createGain(); g2.gain.value = 0.18;
      osc2.connect(g2).connect(master);
      osc2.start();

      calmAudioRef.current = { master, oscs: [osc1, osc2] };
    } catch (e) { /* ignore */ }
    return () => {
      try {
        const a = calmAudioRef.current;
        if (a) {
          a.master.gain.cancelScheduledValues(0);
          a.master.gain.linearRampToValueAtTime(0, calmCtxRef.current.currentTime + 0.8);
          setTimeout(() => {
            a.oscs.forEach((o) => { try { o.stop(); o.disconnect(); } catch (_) {} });
            try { calmCtxRef.current.close(); } catch (_) {}
          }, 900);
        }
      } catch (_) {}
    };
  }, [done]);

  const handleExit = () => navigate("/practices/tapping/select");
  const restart = () => { setStepIdx(0); setDone(false); };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: "radial-gradient(ellipse at 50% 30%, #0A1322 0%, #06101D 50%, #02050B 100%)" }}
      data-testid="tapping-play"
    >
      {/* Star noise */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top controls */}
      <header className="relative z-10 flex items-center justify-between px-5 pt-5">
        <button
          onClick={handleExit}
          className="p-2 -ml-2 rounded-full hover:bg-white/[0.05] transition-colors"
          aria-label="Exit"
          data-testid="tapping-exit"
        >
          <X size={20} className="text-white/45" />
        </button>
      </header>

      {/* End screen */}
      {done ? (
        <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12"
          >
            <CalmOrb />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.6 }}
            className="text-white/85 text-2xl italic"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
          >
            Notice how you feel now
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 1.6 }}
            className="mt-12 flex gap-4"
          >
            <button
              onClick={restart}
              className="px-6 py-2.5 rounded-full text-white text-xs tracking-[0.2em] uppercase border border-white/20 bg-white/[0.06] hover:bg-white/[0.10] transition-colors"
              data-testid="tapping-repeat"
            >
              Repeat
            </button>
            <button
              onClick={restart}
              className="px-6 py-2.5 rounded-full text-white/85 text-xs tracking-[0.2em] uppercase border border-white/15 hover:bg-white/[0.04] transition-colors"
              data-testid="tapping-continue"
            >
              Continue
            </button>
            <button
              onClick={handleExit}
              className="px-6 py-2.5 rounded-full text-white/55 text-xs tracking-[0.2em] uppercase hover:text-white/80 transition-colors"
              data-testid="tapping-return"
            >
              Return
            </button>
          </motion.div>
        </main>
      ) : (
        <>
          {/* Body outline + tapping points */}
          <main className="relative z-10 flex-1 flex items-center justify-center px-6">
            <BodyWithPoints activePoint={current.point} hidden={current.phase === "acknowledge" || current.phase === "settle"} />
          </main>

          {/* Whisper text */}
          <div className="relative z-10 px-8 pb-2 h-20 flex items-center justify-center text-center">
            <AnimatePresence mode="wait">
              <motion.p
                key={`${stepIdx}-${current.text}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.85, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 1.4, ease: "easeOut" }}
                className="text-white/85 text-lg italic max-w-md"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
              >
                {current.text}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Bottom controls */}
          <div className="relative z-10 pb-10 flex flex-col items-center gap-3">
            <button
              onClick={() => setPaused((p) => !p)}
              className="w-12 h-12 rounded-full flex items-center justify-center hover:bg-white/[0.05] transition-colors"
              aria-label={paused ? "Play" : "Pause"}
              data-testid="tapping-pause"
            >
              {paused ? <Play size={18} className="text-white/65" fill="currentColor" /> : <Pause size={18} className="text-white/55" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* --------------------------- Body silhouette ------------------------- */
function BodyWithPoints({ activePoint, hidden }) {
  const W = 360;
  const H = 460;
  const active = TAPPING_POINTS[activePoint];
  const API = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="relative" style={{ width: W, height: H }} data-testid="tapping-body-points">
      {/* Lifelike anatomical body map — Nano-Banana-generated, Aurum chrome-blue palette. */}
      <img
        src={`${API}/api/static/assets/tapping_body_map.png`}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
        style={{ opacity: 0.95, filter: "drop-shadow(0 0 18px rgba(60,110,255,0.12))" }}
        draggable="false"
      />

      {/* Tapping points overlay — positioned by the same TAPPING_POINTS coords
          so the labels & voice timing stay aligned. */}
      {!hidden && TAPPING_POINTS.map((p, i) => {
        const isActive = i === activePoint;
        return (
          <div
            key={p.id}
            className="absolute"
            style={{
              left: `${p.x * 100}%`,
              top: `${p.y * 100}%`,
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Outer pulse */}
            <motion.div
              animate={isActive ? { scale: [1, 2.2, 1], opacity: [0.65, 0, 0.65] } : { scale: 1, opacity: 0 }}
              transition={isActive ? { duration: 1.6, repeat: Infinity, ease: "easeOut" } : { duration: 0.4 }}
              className="absolute inset-0 rounded-full"
              style={{
                width: 24, height: 24,
                left: -12, top: -12,
                background: "radial-gradient(circle, rgba(245,235,210,0.7) 0%, transparent 70%)",
              }}
            />
            {/* Core dot */}
            <motion.div
              animate={isActive ? { scale: 1.35, opacity: 1 } : { scale: 1, opacity: 0.35 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="rounded-full"
              style={{
                width: 9, height: 9,
                background: isActive
                  ? "radial-gradient(circle, rgba(255,245,220,1) 0%, rgba(220,195,150,0.6) 60%, transparent 100%)"
                  : "rgba(190,205,235,0.45)",
                boxShadow: isActive ? "0 0 24px rgba(245,225,180,0.85)" : "0 0 6px rgba(160,180,220,0.3)",
              }}
            />
          </div>
        );
      })}

      {/* Active-point label — floats above the body so the listener
          can see exactly which spot to tap right now. */}
      {!hidden && active && (
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full pointer-events-none"
          style={{
            top: -8,
            background: 'rgba(255,245,225,0.10)',
            border: '1px solid rgba(245,225,180,0.30)',
            backdropFilter: 'blur(8px)',
          }}
          data-testid="tapping-active-label"
        >
          <span className="text-[11px] tracking-[0.22em] uppercase font-medium" style={{ color: 'rgba(255,245,220,0.90)' }}>
            {active.label}
          </span>
        </motion.div>
      )}
    </div>
  );
}

/* Calm settling orb shown after tapping ends */
function CalmOrb() {
  return (
    <motion.div
      animate={{ scale: [1, 1.07, 1], opacity: [0.85, 1, 0.85] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative rounded-full"
      style={{ width: 200, height: 200 }}
    >
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background: "radial-gradient(circle at 50% 45%, rgba(220,235,250,0.95) 0%, rgba(170,195,225,0.5) 38%, transparent 72%)",
          boxShadow: "0 0 80px rgba(170,195,225,0.4)",
        }}
      />
    </motion.div>
  );
}

export default TappingPlay;
